import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft,
  Save,
  X,
  Plus,
  Calendar,
  Clock,
  User,
  Target,
  AlertTriangle,
  FileText,
  Settings,
  Tag,
  Link,
  Paperclip,
  MessageSquare,
  CheckCircle,
  Building,
  Phone,
  Mail,
  MapPin,
  DollarSign,
  Users,
  Briefcase,
  Shield,
  BarChart3,
  Upload,
  Star,
  Award,
  UserCheck,
  FileCheck,
  CreditCard,
  Globe,
  Lock,
  Eye,
  Edit,
  Trash2,
  Database
} from 'lucide-react';
import { useToast, ToastContainer } from '../common/Toast';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import API_CONFIG from '../../config/api';
import VendorService from '../../services/vendorService';

// Helper function to map vendor onboarding form data to vendor schema
const mapOnboardingToVendor = (formData) => {
  // Map business type to legal entity type
  const businessTypeMap = {
    'corporation': 'Corporation',
    'llc': 'LLC',
    'partnership': 'Partnership',
    'sole-proprietorship': 'Sole Proprietorship',
    'non-profit': 'Non-Profit'
  };

  // Map payment terms
  const paymentTermsMap = {
    'net-15': 'Net 15',
    'net-30': 'Net 30',
    'net-45': 'Net 45',
    'net-60': 'Net 60',
    'due-on-receipt': 'Due on Receipt'
  };

  // Helper to clean empty values
  const cleanValue = (value) => {
    if (value === null || value === undefined || value === '') return undefined;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed === '' ? undefined : trimmed;
    }
    return value;
  };

  // Helper to format phone
  const formatPhone = (phone) => {
    if (!phone) return undefined;
    const cleaned = phone.trim();
    if (cleaned === '') return undefined;
    // Remove all non-digit characters
    return cleaned.replace(/\D/g, '');
  };

  // Helper to format website
  const formatWebsite = (website) => {
    if (!website) return undefined;
    const cleaned = website.trim();
    if (cleaned === '') return undefined;
    // If it doesn't start with http:// or https://, add https://
    if (!cleaned.match(/^https?:\/\//i)) {
      if (cleaned.match(/^[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.[a-zA-Z]{2,}/)) {
        return `https://${cleaned}`;
      }
      return undefined;
    }
    try {
      new URL(cleaned);
      return cleaned;
    } catch (e) {
      return undefined;
    }
  };

  // Build vendor data object
  const vendorData = {
    // Required fields
    name: (formData.companyName || formData.legalName || '').trim(),
    vendorType: 'Supplier', // Default for onboarding
  };

  // Add optional basic fields
  if (formData.legalName) {
    vendorData.legalName = cleanValue(formData.legalName);
  }
  if (formData.industry) {
    vendorData.industry = cleanValue(formData.industry);
    vendorData.sector = cleanValue(formData.industry);
  }
  
  // Business Registration
  const businessReg = {};
  if (formData.taxId) businessReg.taxId = cleanValue(formData.taxId);
  if (formData.registrationNumber) businessReg.registrationNumber = cleanValue(formData.registrationNumber);
  if (formData.businessType) businessReg.legalEntityType = businessTypeMap[formData.businessType] || 'Corporation';
  if (formData.headquarters?.country) businessReg.incorporationCountry = cleanValue(formData.headquarters.country);
  if (Object.keys(businessReg).length > 0) {
    vendorData.businessRegistration = businessReg;
  }
  
  // Primary Contact
  const primaryContact = {};
  if (formData.primaryContact?.name) primaryContact.name = cleanValue(formData.primaryContact.name);
  if (formData.primaryContact?.title) primaryContact.title = cleanValue(formData.primaryContact.title);
  if (formData.primaryContact?.email) primaryContact.email = cleanValue(formData.primaryContact.email);
  if (formData.primaryContact?.phone) primaryContact.phone = formatPhone(formData.primaryContact.phone);
  if (formData.primaryContact?.mobile) primaryContact.mobile = formatPhone(formData.primaryContact.mobile);
  if (Object.keys(primaryContact).length > 0) {
    vendorData.primaryContact = primaryContact;
  }
  
  // Addresses
  const addresses = {};
  if (formData.headquarters?.address || formData.headquarters?.city) {
    const hq = {};
    if (formData.headquarters.address) hq.street = cleanValue(formData.headquarters.address);
    if (formData.headquarters.city) hq.city = cleanValue(formData.headquarters.city);
    if (formData.headquarters.state) hq.state = cleanValue(formData.headquarters.state);
    if (formData.headquarters.zipCode) hq.postalCode = cleanValue(formData.headquarters.zipCode);
    if (formData.headquarters.country) hq.country = cleanValue(formData.headquarters.country);
    if (Object.keys(hq).length > 0) addresses.headquarters = hq;
  }
  if (formData.billingAddress?.address || formData.billingAddress?.city) {
    const billing = {};
    if (formData.billingAddress.address) billing.street = cleanValue(formData.billingAddress.address);
    if (formData.billingAddress.city) billing.city = cleanValue(formData.billingAddress.city);
    if (formData.billingAddress.state) billing.state = cleanValue(formData.billingAddress.state);
    if (formData.billingAddress.zipCode) billing.postalCode = cleanValue(formData.billingAddress.zipCode);
    if (formData.billingAddress.country) billing.country = cleanValue(formData.billingAddress.country);
    if (Object.keys(billing).length > 0) addresses.billing = billing;
  }
  if (Object.keys(addresses).length > 0) {
    vendorData.addresses = addresses;
  }
  
  // Communication
  const communication = {};
  if (formData.primaryContact?.email) communication.email = cleanValue(formData.primaryContact.email);
  if (formData.primaryContact?.phone) communication.phone = formatPhone(formData.primaryContact.phone);
  if (formData.website) {
    const websiteValue = formatWebsite(formData.website);
    if (websiteValue) communication.website = websiteValue;
  }
  if (formData.communicationPreferences) {
    if (formData.communicationPreferences.email) communication.preferredMethod = 'Email';
    else if (formData.communicationPreferences.phone) communication.preferredMethod = 'Phone';
    else if (formData.communicationPreferences.portal) communication.preferredMethod = 'Portal';
    else communication.preferredMethod = 'Email';
  }
  if (Object.keys(communication).length > 0) {
    vendorData.communication = communication;
  }
  
  // Business Details
  const businessDetails = {};
  businessDetails.currency = formData.currency || 'USD';
  if (formData.paymentTerms) businessDetails.paymentTerms = paymentTermsMap[formData.paymentTerms] || 'Net 30';
  if (formData.creditRating) businessDetails.creditRating = cleanValue(formData.creditRating);
  if (formData.annualRevenue) {
    const revenue = parseFloat(formData.annualRevenue);
    if (!isNaN(revenue)) businessDetails.annualRevenue = revenue;
  }
  vendorData.businessDetails = businessDetails;
  
  // Banking
  if (formData.bankDetails) {
    const banking = {};
    if (formData.bankDetails.bankName) banking.bankName = cleanValue(formData.bankDetails.bankName);
    if (formData.bankDetails.accountNumber) banking.accountNumber = cleanValue(formData.bankDetails.accountNumber);
    if (formData.bankDetails.routingNumber) banking.routingNumber = cleanValue(formData.bankDetails.routingNumber);
    if (formData.bankDetails.swiftCode) banking.swiftCode = cleanValue(formData.bankDetails.swiftCode);
    if (Object.keys(banking).length > 0) {
      vendorData.banking = banking;
    }
  }
  
  // Service Categories
  if (formData.services && formData.services.length > 0) {
    vendorData.serviceCategories = formData.services.map(service => ({
      category: service,
      subcategories: []
    }));
  }
  
  // Compliance
  const compliance = {};
  if (formData.certifications && formData.certifications.length > 0) {
    compliance.certifications = formData.certifications.map(cert => ({
      name: cert,
      issuingAuthority: '',
      issueDate: null,
      expiryDate: null
    }));
  }
  if (formData.insurancePolicy || formData.insuranceProvider) {
    const insurance = {};
    if (formData.insurancePolicy) insurance.policyNumber = cleanValue(formData.insurancePolicy);
    if (formData.insuranceProvider) insurance.provider = cleanValue(formData.insuranceProvider);
    if (formData.insuranceExpiry) {
      try {
        insurance.expiryDate = new Date(formData.insuranceExpiry);
      } catch (e) {
        // Invalid date, skip
      }
    }
    if (Object.keys(insurance).length > 0) {
      compliance.insurance = insurance;
    }
  }
  if (Object.keys(compliance).length > 0) {
    vendorData.compliance = compliance;
  }
  
  // Notes
  vendorData.performance = {
    ...vendorData.performance,
    notes: cleanValue(formData.description)
  };
  
  // Status
  vendorData.status = 'Pending Approval';
  
  return vendorData;
};

// Helper function to map vendor data from backend to form data
const mapVendorToOnboardingFormData = (vendor) => {
  if (!vendor) return null;

  // Map legal entity type to business type
  const businessTypeMap = {
    'Corporation': 'corporation',
    'LLC': 'llc',
    'Partnership': 'partnership',
    'Sole Proprietorship': 'sole-proprietorship',
    'Non-Profit': 'non-profit'
  };

  // Map payment terms
  const paymentTermsMap = {
    'Net 15': 'net-15',
    'Net 30': 'net-30',
    'Net 45': 'net-45',
    'Net 60': 'net-60',
    'Due on Receipt': 'due-on-receipt'
  };

  return {
    companyName: vendor.name || '',
    legalName: vendor.legalName || vendor.name || '',
    businessType: businessTypeMap[vendor.businessRegistration?.legalEntityType] || '',
    industry: vendor.industry || vendor.sector || '',
    companySize: '', // Not in vendor schema
    yearsInBusiness: '', // Not in vendor schema
    taxId: vendor.businessRegistration?.taxId || '',
    registrationNumber: vendor.businessRegistration?.registrationNumber || '',
    website: vendor.communication?.website || '',
    description: vendor.notes || '',
    
    primaryContact: {
      name: vendor.primaryContact?.name || '',
      title: vendor.primaryContact?.title || '',
      email: vendor.primaryContact?.email || vendor.communication?.email || '',
      phone: vendor.primaryContact?.phone || vendor.communication?.phone || '',
      mobile: vendor.primaryContact?.mobile || ''
    },
    billingContact: {
      name: '',
      title: '',
      email: '',
      phone: ''
    },
    technicalContact: {
      name: '',
      title: '',
      email: '',
      phone: ''
    },
    
    headquarters: {
      address: vendor.addresses?.headquarters?.street || '',
      city: vendor.addresses?.headquarters?.city || '',
      state: vendor.addresses?.headquarters?.state || '',
      zipCode: vendor.addresses?.headquarters?.postalCode || '',
      country: vendor.addresses?.headquarters?.country || vendor.businessRegistration?.incorporationCountry || ''
    },
    billingAddress: {
      address: vendor.addresses?.billing?.street || '',
      city: vendor.addresses?.billing?.city || '',
      state: vendor.addresses?.billing?.state || '',
      zipCode: vendor.addresses?.billing?.postalCode || '',
      country: vendor.addresses?.billing?.country || ''
    },
    shippingAddress: {
      address: vendor.addresses?.shipping?.street || '',
      city: vendor.addresses?.shipping?.city || '',
      state: vendor.addresses?.shipping?.state || '',
      zipCode: vendor.addresses?.shipping?.postalCode || '',
      country: vendor.addresses?.shipping?.country || ''
    },
    
    businessLicense: vendor.businessRegistration?.registrationNumber || '',
    insuranceProvider: vendor.compliance?.insurance?.provider || '',
    insurancePolicy: vendor.compliance?.insurance?.policyNumber || '',
    insuranceExpiry: vendor.compliance?.insurance?.expiryDate ? new Date(vendor.compliance.insurance.expiryDate).toISOString().split('T')[0] : '',
    bankDetails: {
      bankName: vendor.banking?.bankName || '',
      accountNumber: vendor.banking?.accountNumber || '',
      routingNumber: vendor.banking?.routingNumber || '',
      swiftCode: vendor.banking?.swiftCode || ''
    },
    
    services: vendor.serviceCategories?.map(cat => cat.category) || [],
    specialties: vendor.serviceCategories?.map(cat => cat.category) || [],
    certifications: vendor.compliance?.certifications?.map(cert => cert.name) || [],
    licenses: [],
    equipment: [],
    capabilities: [],
    
    annualRevenue: vendor.businessDetails?.annualRevenue ? String(vendor.businessDetails.annualRevenue) : '',
    creditRating: vendor.businessDetails?.creditRating || '',
    paymentTerms: paymentTermsMap[vendor.businessDetails?.paymentTerms] || '',
    currency: vendor.businessDetails?.currency || 'USD',
    preferredPaymentMethod: '',
    creditLimit: '',
    
    complianceCertifications: vendor.compliance?.certifications?.map(cert => cert.name) || [],
    securityClearance: '',
    backgroundChecks: [],
    safetyRecords: [],
    qualityStandards: '',
    
    requiredDocuments: [],
    submittedDocuments: [],
    documentStatus: vendor.status === 'Pending Approval' ? 'pending' : 'submitted',
    reviewNotes: '',
    
    onboardingStage: vendor.status === 'Pending Approval' ? 'review' : vendor.status === 'Active' ? 'completed' : 'initial',
    assignedTo: '',
    priority: 'medium',
    estimatedCompletion: '',
    actualCompletion: vendor.status === 'Active' && vendor.updatedAt ? new Date(vendor.updatedAt).toISOString().split('T')[0] : '',
    
    communicationPreferences: {
      email: vendor.communication?.preferredMethod === 'Email' || true,
      phone: vendor.communication?.preferredMethod === 'Phone' || false,
      sms: false,
      portal: vendor.communication?.preferredMethod === 'Portal' || false
    },
    notificationSettings: {
      projectUpdates: true,
      paymentNotifications: true,
      complianceAlerts: true,
      systemUpdates: false
    },
    
    autoApproval: false,
    preferredProjects: [],
    blacklistedProjects: [],
    customFields: []
  };
};

const CreateVendorOnboarding = ({ editMode = false, viewMode = false, vendorId = null }) => {
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast } = useToast();
  
  // Tab management
  const [activeTab, setActiveTab] = useState('basic');
  
  // Test mode state
  const [testMode, setTestMode] = useState(false);
  const [loading, setLoading] = useState(editMode || viewMode);
  
  // Form state
  const [formData, setFormData] = useState({
    // Basic Information
    companyName: '',
    legalName: '',
    businessType: '',
    industry: '',
    companySize: '',
    yearsInBusiness: '',
    taxId: '',
    registrationNumber: '',
    website: '',
    description: '',
    
    // Contact Information
    primaryContact: {
      name: '',
      title: '',
      email: '',
      phone: '',
      mobile: ''
    },
    billingContact: {
      name: '',
      title: '',
      email: '',
      phone: ''
    },
    technicalContact: {
      name: '',
      title: '',
      email: '',
      phone: ''
    },
    
    // Address Information
    headquarters: {
      address: '',
      city: '',
      state: '',
      zipCode: '',
      country: ''
    },
    billingAddress: {
      address: '',
      city: '',
      state: '',
      zipCode: '',
      country: ''
    },
    shippingAddress: {
      address: '',
      city: '',
      state: '',
      zipCode: '',
      country: ''
    },
    
    // Business Information
    businessLicense: '',
    insuranceProvider: '',
    insurancePolicy: '',
    insuranceExpiry: '',
    bankDetails: {
      bankName: '',
      accountNumber: '',
      routingNumber: '',
      swiftCode: ''
    },
    
    // Capabilities & Services
    services: [],
    specialties: [],
    certifications: [],
    licenses: [],
    equipment: [],
    capabilities: [],
    
    // Financial Information
    annualRevenue: '',
    creditRating: '',
    paymentTerms: '',
    currency: 'USD',
    preferredPaymentMethod: '',
    creditLimit: '',
    
    // Compliance & Security
    complianceCertifications: [],
    securityClearance: '',
    backgroundChecks: [],
    safetyRecords: [],
    qualityStandards: '',
    
    // Documentation
    requiredDocuments: [],
    submittedDocuments: [],
    documentStatus: 'pending',
    reviewNotes: '',
    
    // Onboarding Process
    onboardingStage: 'initial',
    assignedTo: '',
    priority: 'medium',
    estimatedCompletion: '',
    actualCompletion: '',
    
    // Preferences & Settings
    communicationPreferences: {
      email: true,
      phone: false,
      sms: false,
      portal: true
    },
    notificationSettings: {
      projectUpdates: true,
      paymentNotifications: true,
      complianceAlerts: true,
      systemUpdates: false
    },
    
    // Advanced Settings
    autoApproval: false,
    preferredProjects: [],
    blacklistedProjects: [],
    customFields: []
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Helper function to get disabled/readOnly props for form inputs
  const getInputProps = () => ({
    disabled: viewMode,
    readOnly: viewMode
  });

  // Load vendor data if in edit or view mode
  useEffect(() => {
    const loadVendorData = async () => {
      if (vendorId) {
        setLoading(true);
        try {
          const response = await VendorService.getVendor(vendorId);
          if (response && response.success) {
            const mappedData = mapVendorToOnboardingFormData(response.data);
            if (mappedData) {
              setFormData(mappedData);
            }
          } else {
            showError('Failed to load vendor details.');
            navigate('/subcontracting/onboarding');
          }
        } catch (err) {
          console.error('Error loading vendor:', err);
          showError('Failed to load vendor details.');
          navigate('/subcontracting/onboarding');
        } finally {
          setLoading(false);
        }
      }
    };
    
    loadVendorData();
  }, [vendorId]);

  // Load test mode setting on component mount (only in create mode)
  useEffect(() => {
    if (!editMode && !viewMode) {
      const loadTestMode = async () => {
        try {
          const response = await fetch(API_CONFIG.ENDPOINTS.SETTINGS, {
            headers: API_CONFIG.HEADERS
          });
          
          if (response.ok) {
            const result = await response.json();
            if (result.success && result.data) {
              setTestMode(result.data.testMode ?? false);
            }
          }
        } catch (error) {
          console.error('Error loading test mode setting:', error);
        }
      };
      
      loadTestMode();
    }
  }, [editMode, viewMode]);

  // Function to fill form with test data
  const fillTestData = () => {
    const testData = {
      companyName: 'Tech Solutions Inc.',
      legalName: 'Tech Solutions Incorporated',
      businessType: 'corporation',
      industry: 'technology',
      companySize: '51-200',
      yearsInBusiness: '15',
      taxId: '12-3456789',
      registrationNumber: 'REG-2024-001',
      website: 'https://www.techsolutions.com',
      description: 'Leading technology solutions provider specializing in enterprise software development, cloud infrastructure, and digital transformation services.',
      
      primaryContact: {
        name: 'John Smith',
        title: 'Business Development Manager',
        email: 'john.smith@techsolutions.com',
        phone: '555-123-4567',
        mobile: '555-123-4568'
      },
      billingContact: {
        name: 'Jane Doe',
        title: 'Finance Manager',
        email: 'jane.doe@techsolutions.com',
        phone: '555-123-4569'
      },
      technicalContact: {
        name: 'Mike Johnson',
        title: 'Technical Lead',
        email: 'mike.johnson@techsolutions.com',
        phone: '555-123-4570'
      },
      
      headquarters: {
        address: '1234 Innovation Drive, Suite 500',
        city: 'San Francisco',
        state: 'CA',
        zipCode: '94105',
        country: 'United States'
      },
      billingAddress: {
        address: '1234 Innovation Drive, Suite 500',
        city: 'San Francisco',
        state: 'CA',
        zipCode: '94105',
        country: 'United States'
      },
      shippingAddress: {
        address: '1234 Innovation Drive, Suite 500',
        city: 'San Francisco',
        state: 'CA',
        zipCode: '94105',
        country: 'United States'
      },
      
      businessLicense: 'BL-2024-001',
      insuranceProvider: 'Global Insurance Co.',
      insurancePolicy: 'INS-2024-001',
      insuranceExpiry: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 1 year from now
      bankDetails: {
        bankName: 'First National Bank',
        accountNumber: '9876543210',
        routingNumber: '121000248',
        swiftCode: 'FNBOUS33'
      },
      
      services: ['Software Development', 'Cloud Services', 'IT Consulting', 'System Integration'],
      specialties: ['Enterprise Software', 'Cloud Migration', 'DevOps', 'Cybersecurity'],
      certifications: ['ISO 27001', 'SOC 2 Type II', 'AWS Partner', 'Microsoft Gold Partner'],
      licenses: ['Business License', 'Professional Services License'],
      equipment: ['Development Servers', 'Cloud Infrastructure', 'Security Tools'],
      capabilities: ['24/7 Support', 'On-site Services', 'Remote Support'],
      
      annualRevenue: '5000000',
      creditRating: 'A',
      paymentTerms: 'net-30',
      currency: 'USD',
      preferredPaymentMethod: 'ACH',
      creditLimit: '100000',
      
      complianceCertifications: ['ISO 27001', 'SOC 2', 'GDPR Compliance'],
      securityClearance: 'Standard',
      backgroundChecks: ['All employees verified', 'Criminal background checks completed'],
      safetyRecords: ['No incidents in past 5 years', 'OSHA compliant'],
      qualityStandards: 'ISO 9001:2015 certified quality management system',
      
      requiredDocuments: ['Business License', 'Insurance Certificate', 'Tax ID', 'Banking Information'],
      submittedDocuments: [],
      documentStatus: 'pending',
      reviewNotes: '',
      
      onboardingStage: 'initial',
      assignedTo: 'john-doe',
      priority: 'medium',
      estimatedCompletion: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 14 days from now
      actualCompletion: '',
      
      communicationPreferences: {
        email: true,
        phone: true,
        sms: false,
        portal: true
      },
      notificationSettings: {
        projectUpdates: true,
        paymentNotifications: true,
        complianceAlerts: true,
        systemUpdates: false
      },
      
      autoApproval: false,
      preferredProjects: [],
      blacklistedProjects: [],
      customFields: []
    };
    
    setFormData(testData);
    success('Test data filled successfully!');
  };

  // Tab configuration
  const tabs = [
    {
      id: 'basic',
      label: 'Company Information',
      icon: <Building size={18} />
    },
    {
      id: 'contacts',
      label: 'Contact Details',
      icon: <Users size={18} />
    },
    {
      id: 'capabilities',
      label: 'Capabilities & Compliance',
      icon: <Shield size={18} />
    },
    {
      id: 'onboarding',
      label: 'Onboarding Process',
      icon: <UserCheck size={18} />
    }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedInputChange = (parentField, childField, value) => {
    setFormData(prev => ({
      ...prev,
      [parentField]: {
        ...prev[parentField],
        [childField]: value
      }
    }));
  };

  const handleArrayAdd = (field, value) => {
    if (value && value.trim()) {
      setFormData(prev => ({
        ...prev,
        [field]: [...(prev[field] || []), value.trim()]
      }));
    }
  };

  const handleArrayRemove = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: (prev[field] || []).filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Validate required fields
      if (!formData.companyName || !formData.primaryContact?.name || !formData.primaryContact?.email) {
        showError('Please fill in all required fields (Company Name, Primary Contact Name, and Email).');
        setIsSubmitting(false);
        return;
      }

      // Map form data to vendor schema
      const vendorData = mapOnboardingToVendor(formData);

      // Remove undefined and null values recursively
      const removeEmptyValues = (obj) => {
        if (Array.isArray(obj)) {
          return obj.map(removeEmptyValues).filter(item => item !== null && item !== undefined);
        } else if (obj !== null && typeof obj === 'object') {
          const cleaned = {};
          for (const key in obj) {
            const value = removeEmptyValues(obj[key]);
            if (value !== null && value !== undefined && value !== '') {
              cleaned[key] = value;
            }
          }
          return Object.keys(cleaned).length > 0 ? cleaned : undefined;
        }
        return obj === '' ? undefined : obj;
      };

      const finalVendorData = removeEmptyValues(vendorData);

      // Create or update vendor using the service
      let response;
      if (editMode && vendorId) {
        response = await VendorService.updateVendor(vendorId, finalVendorData);
        if (response && response.success) {
          success('Vendor updated successfully!');
          setTimeout(() => {
            navigate('/subcontracting/onboarding');
          }, 1000);
        } else {
          showError(response?.message || 'Failed to update vendor. Please try again.');
        }
      } else {
        response = await VendorService.createVendor(finalVendorData);
        if (response && response.success) {
          success('Vendor onboarding initiated successfully!');
          setTimeout(() => {
            navigate('/subcontracting/onboarding');
          }, 1000);
        } else {
          showError(response?.message || 'Failed to initiate vendor onboarding. Please try again.');
        }
      }
    } catch (err) {
      console.error('Error creating vendor:', err);
      console.error('Error response data:', err?.response?.data);
      console.error('Error response errors:', err?.response?.data?.errors);
      
      // Handle error from service
      let errorMessage = 'Failed to initiate vendor onboarding. Please try again.';
      
      if (err?.response?.data) {
        const errorData = err.response.data;
        
        // Check for validation errors array
        if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
          const validationErrors = errorData.errors.map(e => {
            const field = e.param || e.path || '';
            const msg = e.msg || e.message || 'Invalid value';
            return field ? `${field}: ${msg}` : msg;
          });
          errorMessage = `Validation failed: ${validationErrors.join(', ')}`;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      } else if (err?.message) {
        errorMessage = err.message;
      }
      
      showError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return renderCompanyInformation();
      case 'contacts':
        return renderContactDetails();
      case 'capabilities':
        return renderCapabilitiesCompliance();
      case 'onboarding':
        return renderOnboardingProcess();
      default:
        return renderCompanyInformation();
    }
  };

  const renderCompanyInformation = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Company Information</h3>
        <p className="section-description">Basic company details and business information</p>
      </div>
      
      <div className="form-grid-4">
        <div className="form-group">
          <label className="form-label">Company Name *</label>
          <input
            type="text"
            className="form-control"
            value={formData.companyName}
            onChange={(e) => handleInputChange('companyName', e.target.value)}
            placeholder="Enter company name"
            required
            {...getInputProps()}
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Legal Name</label>
          <input
            type="text"
            className="form-control"
            value={formData.legalName}
            onChange={(e) => handleInputChange('legalName', e.target.value)}
            placeholder="Enter legal company name"
            {...getInputProps()}
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Business Type</label>
          <select
            className="form-select"
            value={formData.businessType}
            onChange={(e) => handleInputChange('businessType', e.target.value)}
            {...getInputProps()}
          >
            <option value="">Select business type</option>
            <option value="corporation">Corporation</option>
            <option value="llc">LLC</option>
            <option value="partnership">Partnership</option>
            <option value="sole-proprietorship">Sole Proprietorship</option>
            <option value="non-profit">Non-Profit</option>
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Industry</label>
          <select
            className="form-select"
            value={formData.industry}
            onChange={(e) => handleInputChange('industry', e.target.value)}
            {...getInputProps()}
          >
            <option value="">Select industry</option>
            <option value="construction">Construction</option>
            <option value="technology">Technology</option>
            <option value="consulting">Consulting</option>
            <option value="manufacturing">Manufacturing</option>
            <option value="healthcare">Healthcare</option>
            <option value="finance">Finance</option>
            <option value="education">Education</option>
            <option value="other">Other</option>
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Company Size</label>
          <select
            className="form-select"
            value={formData.companySize}
            onChange={(e) => handleInputChange('companySize', e.target.value)}
            {...getInputProps()}
          >
            <option value="">Select company size</option>
            <option value="1-10">1-10 employees</option>
            <option value="11-50">11-50 employees</option>
            <option value="51-200">51-200 employees</option>
            <option value="201-500">201-500 employees</option>
            <option value="500+">500+ employees</option>
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Years in Business</label>
          <input
            type="number"
            className="form-control"
            value={formData.yearsInBusiness}
            onChange={(e) => handleInputChange('yearsInBusiness', e.target.value)}
            placeholder="Enter years in business"
            min="0"
            {...getInputProps()}
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Tax ID</label>
          <input
            type="text"
            className="form-control"
            value={formData.taxId}
            onChange={(e) => handleInputChange('taxId', e.target.value)}
            placeholder="Enter tax ID"
            {...getInputProps()}
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Registration Number</label>
          <input
            type="text"
            className="form-control"
            value={formData.registrationNumber}
            onChange={(e) => handleInputChange('registrationNumber', e.target.value)}
            placeholder="Enter registration number"
            {...getInputProps()}
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Website</label>
          <input
            type="url"
            className="form-control"
            value={formData.website}
            onChange={(e) => handleInputChange('website', e.target.value)}
            placeholder="https://www.company.com"
            {...getInputProps()}
          />
        </div>
      </div>
      
      <div className="form-group">
        <label className="form-label">Company Description</label>
        <textarea
          className="form-control"
          rows="4"
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          placeholder="Brief description of the company and its services"
          {...getInputProps()}
        />
      </div>
      
      <div className="form-grid-2">
        <div className="form-group">
          <label className="form-label">Business License</label>
          <input
            type="text"
            className="form-control"
            value={formData.businessLicense}
            onChange={(e) => handleInputChange('businessLicense', e.target.value)}
            placeholder="Enter business license number"
            {...getInputProps()}
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Insurance Provider</label>
          <input
            type="text"
            className="form-control"
            value={formData.insuranceProvider}
            onChange={(e) => handleInputChange('insuranceProvider', e.target.value)}
            placeholder="Enter insurance provider"
            {...getInputProps()}
          />
        </div>
      </div>
    </div>
  );

  const renderContactDetails = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Contact Information</h3>
        <p className="section-description">Primary, billing, and technical contact details</p>
      </div>
      
      <div className="form-group">
        <h4 className="section-subtitle">Primary Contact</h4>
        <div className="form-grid-4">
          <div className="form-group">
            <label className="form-label">Name *</label>
            <input
              type="text"
              className="form-control"
              value={formData.primaryContact.name}
              onChange={(e) => handleNestedInputChange('primaryContact', 'name', e.target.value)}
              placeholder="Enter full name"
              required
              {...getInputProps()}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Title</label>
            <input
              type="text"
              className="form-control"
              value={formData.primaryContact.title}
              onChange={(e) => handleNestedInputChange('primaryContact', 'title', e.target.value)}
              placeholder="Enter job title"
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Email *</label>
            <input
              type="email"
              className="form-control"
              value={formData.primaryContact.email}
              onChange={(e) => handleNestedInputChange('primaryContact', 'email', e.target.value)}
              placeholder="Enter email address"
              required
              {...getInputProps()}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Phone</label>
            <input
              type="tel"
              className="form-control"
              value={formData.primaryContact.phone}
              onChange={(e) => handleNestedInputChange('primaryContact', 'phone', e.target.value)}
              placeholder="Enter phone number"
            />
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <h4 className="section-subtitle">Billing Contact</h4>
        <div className="form-grid-4">
          <div className="form-group">
            <label className="form-label">Name</label>
            <input
              type="text"
              className="form-control"
              value={formData.billingContact.name}
              onChange={(e) => handleNestedInputChange('billingContact', 'name', e.target.value)}
              placeholder="Enter full name"
              {...getInputProps()}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Title</label>
            <input
              type="text"
              className="form-control"
              value={formData.billingContact.title}
              onChange={(e) => handleNestedInputChange('billingContact', 'title', e.target.value)}
              placeholder="Enter job title"
              {...getInputProps()}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Email</label>
            <input
              type="email"
              className="form-control"
              value={formData.billingContact.email}
              onChange={(e) => handleNestedInputChange('billingContact', 'email', e.target.value)}
              placeholder="Enter email address"
              {...getInputProps()}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Phone</label>
            <input
              type="tel"
              className="form-control"
              value={formData.billingContact.phone}
              onChange={(e) => handleNestedInputChange('billingContact', 'phone', e.target.value)}
              placeholder="Enter phone number"
              {...getInputProps()}
            />
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <h4 className="section-subtitle">Address Information</h4>
        <div className="form-grid-2">
          <div className="form-group">
            <label className="form-label">Headquarters Address</label>
            <textarea
              className="form-control"
              rows="3"
              value={formData.headquarters.address}
              onChange={(e) => handleNestedInputChange('headquarters', 'address', e.target.value)}
              placeholder="Enter headquarters address"
              {...getInputProps()}
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Billing Address</label>
            <textarea
              className="form-control"
              rows="3"
              value={formData.billingAddress.address}
              onChange={(e) => handleNestedInputChange('billingAddress', 'address', e.target.value)}
              placeholder="Enter billing address"
              {...getInputProps()}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderCapabilitiesCompliance = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Capabilities & Compliance</h3>
        <p className="section-description">Services, certifications, and compliance requirements</p>
      </div>
      
      <div className="form-group">
        <label className="form-label">Services Offered</label>
        <div className="array-input-group">
          {formData.services.map((service, index) => (
            <div key={index} className="array-input-item">
              <input
                type="text"
                className="form-control"
                value={service}
                onChange={(e) => {
                  const newServices = [...formData.services];
                  newServices[index] = e.target.value;
                  setFormData(prev => ({ ...prev, services: newServices }));
                }}
                placeholder="Enter service"
                {...getInputProps()}
              />
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={() => handleArrayRemove('services', index)}
              >
                <X size={16} />
              </button>
            </div>
          ))}
          <div className="array-input-add">
            <input
              type="text"
              className="form-control"
              placeholder="Add new service"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleArrayAdd('services', e.target.value);
                  e.target.value = '';
                }
              }}
            />
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={(e) => {
                const input = e.target.previousElementSibling;
                if (input && input.value) {
                  handleArrayAdd('services', input.value);
                  input.value = '';
                }
              }}
            >
              <Plus size={16} />
            </button>
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <label className="form-label">Specialties</label>
        <div className="array-input-group">
          {formData.specialties.map((specialty, index) => (
            <div key={index} className="array-input-item">
              <input
                type="text"
                className="form-control"
                value={specialty}
                onChange={(e) => {
                  const newSpecialties = [...formData.specialties];
                  newSpecialties[index] = e.target.value;
                  setFormData(prev => ({ ...prev, specialties: newSpecialties }));
                }}
                placeholder="Enter specialty"
                {...getInputProps()}
              />
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={() => handleArrayRemove('specialties', index)}
              >
                <X size={16} />
              </button>
            </div>
          ))}
          <div className="array-input-add">
            <input
              type="text"
              className="form-control"
              placeholder="Add new specialty"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleArrayAdd('specialties', e.target.value);
                  e.target.value = '';
                }
              }}
            />
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={(e) => {
                const input = e.target.previousElementSibling;
                if (input && input.value) {
                  handleArrayAdd('specialties', input.value);
                  input.value = '';
                }
              }}
            >
              <Plus size={16} />
            </button>
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <label className="form-label">Certifications</label>
        <div className="array-input-group">
          {formData.certifications.map((cert, index) => (
            <div key={index} className="array-input-item">
              <input
                type="text"
                className="form-control"
                value={cert}
                onChange={(e) => {
                  const newCerts = [...formData.certifications];
                  newCerts[index] = e.target.value;
                  setFormData(prev => ({ ...prev, certifications: newCerts }));
                }}
                placeholder="Enter certification"
                {...getInputProps()}
              />
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={() => handleArrayRemove('certifications', index)}
              >
                <X size={16} />
              </button>
            </div>
          ))}
          <div className="array-input-add">
            <input
              type="text"
              className="form-control"
              placeholder="Add new certification"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleArrayAdd('certifications', e.target.value);
                  e.target.value = '';
                }
              }}
            />
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={(e) => {
                const input = e.target.previousElementSibling;
                if (input && input.value) {
                  handleArrayAdd('certifications', input.value);
                  input.value = '';
                }
              }}
            >
              <Plus size={16} />
            </button>
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <label className="form-label">Quality Standards</label>
        <textarea
          className="form-control"
          rows="3"
          value={formData.qualityStandards}
          onChange={(e) => handleInputChange('qualityStandards', e.target.value)}
          placeholder="Describe quality standards and processes"
          {...getInputProps()}
        />
      </div>
    </div>
  );

  const renderOnboardingProcess = () => (
    <div className="project-form-section">
      <div className="section-header">
        <h3 className="section-title">Onboarding Process</h3>
        <p className="section-description">Onboarding workflow, assignments, and process management</p>
      </div>
      
      <div className="form-grid-4">
        <div className="form-group">
          <label className="form-label">Onboarding Stage</label>
          <select
            className="form-select"
            value={formData.onboardingStage}
            onChange={(e) => handleInputChange('onboardingStage', e.target.value)}
            {...getInputProps()}
          >
            <option value="initial">Initial</option>
            <option value="documentation">Documentation</option>
            <option value="review">Review</option>
            <option value="approval">Approval</option>
            <option value="completed">Completed</option>
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Assigned To</label>
          <select
            className="form-select"
            value={formData.assignedTo}
            onChange={(e) => handleInputChange('assignedTo', e.target.value)}
            {...getInputProps()}
          >
            <option value="">Select assignee</option>
            <option value="john-doe">John Doe</option>
            <option value="jane-smith">Jane Smith</option>
            <option value="mike-johnson">Mike Johnson</option>
            <option value="sarah-wilson">Sarah Wilson</option>
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Priority</label>
          <select
            className="form-select"
            value={formData.priority}
            onChange={(e) => handleInputChange('priority', e.target.value)}
            {...getInputProps()}
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Document Status</label>
          <select
            className="form-select"
            value={formData.documentStatus}
            onChange={(e) => handleInputChange('documentStatus', e.target.value)}
            {...getInputProps()}
          >
            <option value="pending">Pending</option>
            <option value="submitted">Submitted</option>
            <option value="reviewed">Reviewed</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Estimated Completion</label>
          <input
            type="date"
            className="form-control"
            value={formData.estimatedCompletion}
            onChange={(e) => handleInputChange('estimatedCompletion', e.target.value)}
            {...getInputProps()}
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Actual Completion</label>
          <input
            type="date"
            className="form-control"
            value={formData.actualCompletion}
            onChange={(e) => handleInputChange('actualCompletion', e.target.value)}
            {...getInputProps()}
          />
        </div>
      </div>
      
      <div className="form-group">
        <label className="form-label">Review Notes</label>
        <textarea
          className="form-control"
          rows="4"
          value={formData.reviewNotes}
          onChange={(e) => handleInputChange('reviewNotes', e.target.value)}
          placeholder="Enter review notes and comments"
          {...getInputProps()}
        />
      </div>
      
      <div className="form-group">
        <label className="form-label">Communication Preferences</label>
        <div className="checkbox-group">
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              checked={formData.communicationPreferences.email}
              onChange={(e) => handleNestedInputChange('communicationPreferences', 'email', e.target.checked)}
              disabled={viewMode}
            />
            <label className="form-check-label">Email</label>
          </div>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              checked={formData.communicationPreferences.phone}
              onChange={(e) => handleNestedInputChange('communicationPreferences', 'phone', e.target.checked)}
              disabled={viewMode}
            />
            <label className="form-check-label">Phone</label>
          </div>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              checked={formData.communicationPreferences.sms}
              onChange={(e) => handleNestedInputChange('communicationPreferences', 'sms', e.target.checked)}
              disabled={viewMode}
            />
            <label className="form-check-label">SMS</label>
          </div>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              checked={formData.communicationPreferences.portal}
              onChange={(e) => handleNestedInputChange('communicationPreferences', 'portal', e.target.checked)}
              disabled={viewMode}
            />
            <label className="form-check-label">Portal</label>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader 
        title={viewMode ? "View Vendor Onboarding" : editMode ? "Edit Vendor Onboarding" : "Vendor Onboarding"}
        description="Streamlined vendor registration and approval process"
      />
      <div className={`page-container ${viewMode ? 'view-mode' : ''}`} style={{ width: '100%', maxWidth: '100%' }}>
        {/* Main Content */}
        <div className="project-create-container" style={{ width: '100%', maxWidth: '100%' }}>
        <form onSubmit={handleSubmit} style={{ width: '100%' }} className={viewMode ? 'view-mode' : ''}>
          {/* Tab Navigation */}
          <div className="project-tabs">
            <div className="project-tabs-nav">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`project-tab ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <div className="project-tab-icon">
                    {tab.icon}
                  </div>
                  <div className="project-tab-content">
                    <div className="project-tab-label">{tab.label}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="project-form-card">
            {renderTabContent()}
          </div>

          {/* Form Actions */}
          <div className="project-form-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              {testMode && !editMode && !viewMode && (
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={fillTestData}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    backgroundColor: '#ffffff',
                    borderColor: '#d1d5db'
                  }}
                >
                  <Database size={18} />
                  Fill Test Data
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              {viewMode ? (
                <>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => navigate('/subcontracting/onboarding')}
                  >
                    <ArrowLeft size={18} />
                    Back to List
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => navigate(`/subcontracting/onboarding/edit/${vendorId}`)}
                    disabled={!vendorId || viewMode}
                  >
                    <Edit size={18} />
                    Edit Vendor
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => navigate('/subcontracting/onboarding')}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting || viewMode}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="spinner-border spinner-border-sm me-2" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        {editMode ? 'Updating...' : 'Initiating Onboarding...'}
                      </>
                    ) : (
                      <>
                        <Save size={18} />
                        {editMode ? 'Update Vendor' : 'Initiate Onboarding'}
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </form>
        </div>
      </div>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      {viewMode && (
        <style>{`
          .view-mode input,
          .view-mode select,
          .view-mode textarea {
            pointer-events: none;
            opacity: 0.7;
            background-color: #f3f4f6 !important;
            cursor: not-allowed;
          }
          .view-mode .array-input-add,
          .view-mode .btn-outline-danger {
            display: none !important;
          }
          .view-mode button[type="submit"] {
            display: none !important;
          }
        `}</style>
      )}
    </>
  );
};

export default CreateVendorOnboarding;
